import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { useDispatch, useSelector } from 'react-redux';

import 'react-datepicker/dist/react-datepicker.css';

import { obtenerDetalleProyecto, editarProyecto, limpiarDetalleProyecto } from '../../../actions/proyectos';

const formatYYYYMMDD = (d) => {
    if (!d) return null;
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const parseFecha = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

const EditarProyectoModal = ({ show, onHide, onSuccess, idProyecto, opcionesClientes }) => {
    const dispatch = useDispatch();
    const { detalle, loadingDetalle } = useSelector(state => state.proyectos);

    const [clienteSel, setClienteSel] = useState(null);
    const [nombre, setNombre] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [direccion, setDireccion] = useState('');
    const [fechaInicioTentativa, setFechaInicioTentativa] = useState(null);
    const [fechaFinTentativa, setFechaFinTentativa] = useState(null);
    const [fechaInicioReal, setFechaInicioReal] = useState(null);
    const [fechaFinReal, setFechaFinReal] = useState(null);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        if (!show) return;

        if (!idProyecto) return;

        dispatch(obtenerDetalleProyecto(idProyecto));
    }, [show, idProyecto, dispatch]);

    useEffect(() => {
        if (!show || !detalle) return;

        setClienteSel(detalle?.cliente?._id ? { value: detalle.cliente._id, label: detalle.cliente.nombre } : null);
        setNombre(detalle?.nombre || '');
        setDescripcion(detalle?.descripcion || '');
        setDireccion(detalle?.direccion || '');
        setFechaInicioTentativa(parseFecha(detalle?.fecha_inicio_tentativa));
        setFechaFinTentativa(parseFecha(detalle?.fecha_fin_tentativa));
        setFechaInicioReal(parseFecha(detalle?.fecha_inicio_real));
        setFechaFinReal(parseFecha(detalle?.fecha_fin_real));
    }, [show, detalle]);

    useEffect(() => {
        if (show) return;

        setClienteSel(null);
        setNombre('');
        setDescripcion('');
        setDireccion('');
        setFechaInicioTentativa(null);
        setFechaFinTentativa(null);
        setFechaInicioReal(null);
        setFechaFinReal(null);
        setGuardando(false);
        dispatch(limpiarDetalleProyecto());
    }, [show, dispatch]);

    const puedeGuardar = useMemo(() => {
        return Boolean(clienteSel?.value && nombre.trim().length > 0 && !guardando && !loadingDetalle);
    }, [clienteSel, nombre, guardando, loadingDetalle]);

    const onGuardar = async () => {
        if (!puedeGuardar || !idProyecto) return;

        setGuardando(true);
        const ok = await dispatch(editarProyecto({
            id: idProyecto,
            id_cliente: clienteSel?.value,
            nombre: nombre.trim(),
            descripcion: descripcion.trim() || null,
            direccion: direccion.trim() || null,
            fecha_inicio_tentativa: formatYYYYMMDD(fechaInicioTentativa),
            fecha_fin_tentativa: formatYYYYMMDD(fechaFinTentativa),
            fecha_inicio_real: formatYYYYMMDD(fechaInicioReal),
            fecha_fin_real: formatYYYYMMDD(fechaFinReal),
            status: detalle?.status ?? null
        }));
        setGuardando(false);

        if (!ok) return;

        if (typeof onSuccess === 'function') {
            onSuccess();
        } else if (typeof onHide === 'function') {
            onHide();
        }
    };

    return (
        <Modal show={show} onHide={onHide} backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>Editar proyecto</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {loadingDetalle ? (
                    <div className="d-flex justify-content-center py-4">
                        <div className="spinner-border" role="status" />
                    </div>
                ) : (
                    <>
                        <div className="mb-3">
                            <div className="text-muted small mb-1">Cliente</div>
                            <Select
                                options={opcionesClientes}
                                value={clienteSel}
                                onChange={setClienteSel}
                                isClearable
                                placeholder="Cliente..."
                                noOptionsMessage={() => 'Sin resultados'}
                            />
                        </div>

                        <div className="mb-3">
                            <div className="text-muted small mb-1">Nombre del proyecto</div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Nombre del proyecto"
                                value={nombre}
                                onChange={(e) => setNombre(e.target.value)}
                            />
                        </div>

                        <div className="mb-3">
                            <div className="text-muted small mb-1">Descripción</div>
                            <textarea
                                className="form-control"
                                placeholder="Descripción"
                                rows={3}
                                value={descripcion}
                                onChange={(e) => setDescripcion(e.target.value)}
                            />
                        </div>

                        <div className="mb-3">
                            <div className="text-muted small mb-1">Dirección</div>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Dirección"
                                value={direccion}
                                onChange={(e) => setDireccion(e.target.value)}
                            />
                        </div>

                        <div className="row">
                            <div className="col-6 mb-3">
                                <div className="text-muted small mb-1">Fecha inicio tentativa</div>
                                <DatePicker
                                    selected={fechaInicioTentativa}
                                    onChange={(d) => setFechaInicioTentativa(d)}
                                    className="form-control"
                                    placeholderText="Fecha inicio tentativa"
                                    dateFormat="dd/MM/yyyy"
                                    isClearable
                                />
                            </div>
                            <div className="col-6 mb-3">
                                <div className="text-muted small mb-1">Fecha fin tentativa</div>
                                <DatePicker
                                    selected={fechaFinTentativa}
                                    onChange={(d) => setFechaFinTentativa(d)}
                                    className="form-control"
                                    placeholderText="Fecha fin tentativa"
                                    dateFormat="dd/MM/yyyy"
                                    isClearable
                                />
                            </div>
                            <div className="col-6 mb-3">
                                <div className="text-muted small mb-1">Fecha inicio real</div>
                                <DatePicker
                                    selected={fechaInicioReal}
                                    onChange={(d) => setFechaInicioReal(d)}
                                    className="form-control"
                                    placeholderText="Fecha inicio real"
                                    dateFormat="dd/MM/yyyy"
                                    isClearable
                                />
                            </div>
                            <div className="col-6 mb-3">
                                <div className="text-muted small mb-1">Fecha fin real</div>
                                <DatePicker
                                    selected={fechaFinReal}
                                    onChange={(d) => setFechaFinReal(d)}
                                    className="form-control"
                                    placeholderText="Fecha fin real"
                                    dateFormat="dd/MM/yyyy"
                                    isClearable
                                />
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onHide} disabled={guardando}>
                    Cancelar
                </button>
                <button type="button" className="btn btn-primary" onClick={onGuardar} disabled={!puedeGuardar}>
                    Guardar
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default EditarProyectoModal;
